import React from 'react'
import {Linking, Pressable, StyleSheet, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {type CommonNavigatorParams} from '#/lib/routes/types'
import {useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Check_Stroke2_Corner0_Rounded as CheckIcon} from '#/components/icons/Check'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'

// Invite system worker URL for fetching user_id
const INVITE_WORKER_URL = 'https://batesky-invite-system.attps.workers.dev'

/**
 * Fetch the user's obfuscated user_id from the invite system
 * This is used instead of sending the DID/username directly to payment providers
 */
async function fetchUserId(userDid: string): Promise<string | null> {
  try {
    const response = await fetch(`${INVITE_WORKER_URL}/get-user-id`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({userDid}),
    })

    if (!response.ok) {
      console.error('Failed to fetch user_id:', response.status)
      return null
    }

    const result = (await response.json()) as {userId?: string}
    return result.userId || null
  } catch (error) {
    console.error('Error fetching user_id:', error)
    return null
  }
}

// Plan configuration
type BillingType = 'one-time' | 'recurring'
type PlanType = 'month' | 'annual'
type PaymentProvider = 'cardbilling' | 'nowpayments'

const PLANS = {
  month: {
    price: '6.99',
    currency: 'EUR',
    period: 30,
    label: 'Month',
  },
  annual: {
    price: '69.99',
    currency: 'EUR',
    period: 365,
    label: 'Annual',
  },
}

type Props = NativeStackScreenProps<CommonNavigatorParams, 'Premium'>

export function PremiumScreen({}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const {currentAccount} = useSession()
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // New state for plan selection
  const [billingType, setBillingType] = React.useState<BillingType>('one-time')
  const [selectedPlan, setSelectedPlan] = React.useState<PlanType | null>(null)
  const [paymentProvider, setPaymentProvider] =
    React.useState<PaymentProvider | null>(null)

  // Clear nowpayments selection when switching to recurring
  const handleBillingTypeChange = React.useCallback(
    (newBillingType: BillingType) => {
      setBillingType(newBillingType)
      if (newBillingType === 'recurring' && paymentProvider === 'nowpayments') {
        setPaymentProvider(null)
      }
    },
    [paymentProvider],
  )

  const handleContinuePurchase = React.useCallback(async () => {
    if (!currentAccount) return

    setIsLoading(true)
    setError(null)

    try {
      const userId = await fetchUserId(currentAccount.did)
      if (!userId) {
        setError('Unable to process payment. Please try again later.')
        return
      }

      if (!selectedPlan || !paymentProvider) {
        setError('Please select a plan and payment provider.')
        return
      }

      const plan = PLANS[selectedPlan]
      const isRecurring = billingType === 'recurring'

      // TODO: Integrate actual payment providers
      // For now, log the selection and show a placeholder message
      console.log('Payment request:', {
        userId,
        plan,
        isRecurring,
        paymentProvider,
      })

      if (paymentProvider === 'cardbilling') {
        // TODO: Integrate CardBilling API
        setError('CardBilling integration coming soon. Please try again later.')
      } else {
        // NOWPayments crypto payment - include order_id for IPN callback identification
        const nowPaymentsUrl =
          selectedPlan === 'annual'
            ? `https://nowpayments.io/payment/?iid=5327319951&order_id=${userId}`
            : `https://nowpayments.io/payment/?iid=6301425838&order_id=${userId}`
        await Linking.openURL(nowPaymentsUrl)
      }
    } catch (err) {
      console.error('Payment error:', err)
      setError('Unable to process payment. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }, [currentAccount, selectedPlan, billingType, paymentProvider])

  return (
    <Layout.Screen>
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Join Premium</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>
      <Layout.Content>
        <View style={[a.p_xl, a.gap_xl]}>
          {/* Premium Features Section */}
          <View style={[a.gap_md]}>
            <Text style={[a.text_xl, a.font_bold, t.atoms.text]}>
              <Trans>Premium Features</Trans>
            </Text>

            <View style={[a.gap_sm]}>
              <FeatureItem checked label={_(msg`Batefeed`)} />
              <FeatureItem checked label={_(msg`1-on-1 Rooms`)} />
              <FeatureItem
                checked={false}
                label={_(msg`Group Rooms (coming soon)!`)}
              />
            </View>
          </View>

          {/* Premium Plans Section */}
          <View style={[a.gap_md]}>
            <Text style={[a.text_xl, a.font_bold, t.atoms.text]}>
              <Trans>Premium Plans</Trans>
            </Text>

            {/* Billing Type Toggle */}
            <BillingTypeToggle
              value={billingType}
              onChange={handleBillingTypeChange}
            />

            {/* Plan Selection */}
            <View style={[a.gap_sm]}>
              <PlanCard
                selected={selectedPlan === 'month'}
                onSelect={() =>
                  setSelectedPlan(selectedPlan === 'month' ? null : 'month')
                }
                label={_(msg`Month`)}
                price="EUR 6.99"
                duration={_(msg`30 days`)}
              />
              <PlanCard
                selected={selectedPlan === 'annual'}
                onSelect={() =>
                  setSelectedPlan(selectedPlan === 'annual' ? null : 'annual')
                }
                label={_(msg`Annual`)}
                price="EUR 69.99"
                duration={_(msg`365 days`)}
                badge={_(msg`2 MONTHS FREE ON ANNUAL`)}
              />
            </View>
          </View>

          {/* Payment Provider Section */}
          <View style={[a.gap_md]}>
            <Text style={[a.text_xl, a.font_bold, t.atoms.text]}>
              <Trans>Choose a payment provider</Trans>
            </Text>

            <View style={[a.gap_sm]}>
              <PaymentProviderCard
                selected={paymentProvider === 'cardbilling'}
                onSelect={() =>
                  setPaymentProvider(
                    paymentProvider === 'cardbilling' ? null : 'cardbilling',
                  )
                }
                name="CardBilling"
                methods={_(msg`Visa, Mastercard`)}
                billingDescriptor="TBD.com*ATTPS"
              />
              <PaymentProviderCard
                selected={paymentProvider === 'nowpayments'}
                onSelect={() =>
                  setPaymentProvider(
                    paymentProvider === 'nowpayments' ? null : 'nowpayments',
                  )
                }
                name="NOWPayments"
                methods={_(msg`Cryptocurrency`)}
                disabled={billingType === 'recurring'}
              />
            </View>
          </View>

          {/* Error Message */}
          {error && (
            <Text
              style={[
                a.text_sm,
                a.text_center,
                {color: t.palette.negative_500},
              ]}>
              {error}
            </Text>
          )}

          {/* Continue Purchase Button */}
          <Button
            label={_(msg`Continue Purchase`)}
            onPress={handleContinuePurchase}
            size="large"
            variant="solid"
            color="primary"
            disabled={isLoading}
            style={[a.rounded_md, styles.continueButton]}>
            <ButtonText>
              {isLoading ? (
                <Trans>Loading...</Trans>
              ) : (
                <Trans>Continue Purchase</Trans>
              )}
            </ButtonText>
          </Button>
        </View>
      </Layout.Content>
    </Layout.Screen>
  )
}

function FeatureItem({checked, label}: {checked: boolean; label: string}) {
  const t = useTheme()

  return (
    <View style={[a.flex_row, a.align_center, a.gap_sm]}>
      {checked ? (
        <CheckIcon size="md" style={{color: t.palette.positive_600}} />
      ) : (
        <View style={styles.emptyCircle} />
      )}
      <Text
        style={[
          a.text_md,
          checked ? t.atoms.text : t.atoms.text_contrast_medium,
        ]}>
        {label}
      </Text>
    </View>
  )
}

function BillingTypeToggle({
  value,
  onChange,
}: {
  value: BillingType
  onChange: (value: BillingType) => void
}) {
  const t = useTheme()

  return (
    <View
      style={[
        styles.toggleContainer,
        {backgroundColor: t.palette.contrast_50},
      ]}>
      <Pressable
        style={[
          styles.toggleOption,
          value === 'one-time' && [
            styles.toggleOptionActive,
            {backgroundColor: t.palette.primary_500},
          ],
        ]}
        onPress={() => onChange('one-time')}
        accessibilityRole="button"
        accessibilityLabel="One-time payment"
        accessibilityHint="Select one-time payment option"
        accessibilityState={{selected: value === 'one-time'}}>
        <Text
          style={[
            a.text_sm,
            a.font_bold,
            value === 'one-time'
              ? {color: '#fff'}
              : t.atoms.text_contrast_medium,
          ]}>
          <Trans>One-time</Trans>
        </Text>
      </Pressable>
      <Pressable
        style={[
          styles.toggleOption,
          value === 'recurring' && [
            styles.toggleOptionActive,
            {backgroundColor: t.palette.primary_500},
          ],
        ]}
        onPress={() => onChange('recurring')}
        accessibilityRole="button"
        accessibilityLabel="Recurring subscription"
        accessibilityHint="Select recurring subscription option"
        accessibilityState={{selected: value === 'recurring'}}>
        <Text
          style={[
            a.text_sm,
            a.font_bold,
            value === 'recurring'
              ? {color: '#fff'}
              : t.atoms.text_contrast_medium,
          ]}>
          <Trans>Recurring</Trans>
        </Text>
      </Pressable>
    </View>
  )
}

function PlanCard({
  selected,
  onSelect,
  label,
  price,
  duration,
  badge,
}: {
  selected: boolean
  onSelect: () => void
  label: string
  price: string
  duration: string
  badge?: string
}) {
  const t = useTheme()

  return (
    <Pressable
      style={[
        styles.planCard,
        {
          backgroundColor: t.palette.contrast_25,
          borderColor: selected
            ? t.palette.primary_500
            : t.palette.contrast_100,
        },
      ]}
      onPress={onSelect}
      accessibilityRole="radio"
      accessibilityLabel={`${label} plan, ${price}, ${duration}`}
      accessibilityHint="Double tap to select this plan"
      accessibilityState={{selected}}>
      <View style={[a.flex_row, a.gap_md]}>
        <View
          style={[
            styles.radioOuter,
            {
              borderColor: selected
                ? t.palette.primary_500
                : t.palette.contrast_300,
            },
          ]}>
          {selected && (
            <View
              style={[
                styles.radioInner,
                {backgroundColor: t.palette.primary_500},
              ]}
            />
          )}
        </View>
        <View style={[a.flex_1, a.gap_sm]}>
          <View style={[a.flex_row, a.align_center, a.justify_between]}>
            <Text style={[a.text_md, a.font_bold, t.atoms.text]}>{label}</Text>
            <Text style={[a.text_md, a.font_bold, t.atoms.text]}>{price}</Text>
          </View>
          <View style={[a.flex_row, a.align_center, a.gap_sm]}>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {duration}
            </Text>
            {badge && (
              <View
                style={[
                  styles.badge,
                  {
                    backgroundColor: 'transparent',
                    borderWidth: 1,
                    borderColor: '#423125',
                  },
                ]}>
                <Text
                  style={[
                    a.text_xs,
                    a.font_bold,
                    t.atoms.text_contrast_medium,
                  ]}>
                  {badge}
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>
    </Pressable>
  )
}

function PaymentProviderCard({
  selected,
  onSelect,
  name,
  methods,
  billingDescriptor,
  disabled,
}: {
  selected: boolean
  onSelect: () => void
  name: string
  methods: string
  billingDescriptor?: string
  disabled?: boolean
}) {
  const t = useTheme()

  return (
    <Pressable
      style={[
        styles.providerCard,
        {
          backgroundColor: t.palette.contrast_25,
          borderColor: selected
            ? t.palette.primary_500
            : t.palette.contrast_100,
          opacity: disabled ? 0.4 : 1,
        },
      ]}
      onPress={disabled ? undefined : onSelect}
      disabled={disabled}
      accessibilityRole="radio"
      accessibilityLabel={`${name} payment provider, accepts ${methods}`}
      accessibilityHint="Double tap to select this payment provider"
      accessibilityState={{selected, disabled}}>
      <View style={[a.flex_row, a.gap_md]}>
        <View
          style={[
            styles.radioOuter,
            {
              borderColor: selected
                ? t.palette.primary_500
                : t.palette.contrast_300,
            },
          ]}>
          {selected && (
            <View
              style={[
                styles.radioInner,
                {backgroundColor: t.palette.primary_500},
              ]}
            />
          )}
        </View>
        <View style={[a.flex_1, a.flex_row, a.gap_sm]}>
          <View style={[a.flex_1, a.gap_sm]}>
            <Text style={[a.text_md, a.font_bold, t.atoms.text]}>{name}</Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {methods}
            </Text>
          </View>
          {billingDescriptor && (
            <View style={[a.flex_1, a.gap_sm]}>
              <Text style={[a.text_sm, a.font_bold, t.atoms.text]}>
                {billingDescriptor}
              </Text>
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                <Trans>Your card will be billed as</Trans>
              </Text>
            </View>
          )}
        </View>
      </View>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  emptyCircle: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#888',
  },
  toggleContainer: {
    flexDirection: 'row',
    borderRadius: 8,
    padding: 4,
  },
  toggleOption: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: 'center',
  },
  toggleOptionActive: {
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  planCard: {
    borderRadius: 12,
    borderWidth: 2,
    padding: 16,
  },
  providerCard: {
    borderRadius: 12,
    borderWidth: 2,
    padding: 16,
  },
  radioOuter: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioInner: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  badge: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 20,
  },
  continueButton: {
    marginTop: 8,
  },
})
